/**
 * Web checking state: the twin of `HealthCheckProgress.tsx`. Separate from
 * the score: while a review runs there is no gauge, only
 *
 *   ♥ CV Health                     (optional identity; the heart works)
 *   Checking skills and clarity…    the live stage, announced politely
 *   ∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿∿ ─ ·      share of checks done, never a timer
 *   Checks completed        24/26   what the line counts, once it is known
 *   ✓ Writing and experience        four named steps on a rail, with
 *   ✓ Sections and contact details  Linear-style marks: done (a quiet tick),
 *   ◐ Skills and clarity            active (half filled, its row lifted)
 *     The last checks can take…     and pending (an outline)
 *   ○ Your results
 *   Your original CV stays unchanged.
 *
 * The line is Material 3 Expressive's wavy determinate indicator: its length
 * is the real share of checks done, and the wave travels toward the end
 * while the check runs. In practice the first poll already has every
 * deterministic check done and the line then holds for the whole AI pass,
 * so the travelling wave (and the heart) are what show it is still working;
 * once ready it settles into a flat line. Reduced motion stills both. Violet
 * marks what is happening now: done steps go quiet, never green ("checked",
 * not "passed"). With the identity (onboarding, a page of its own) the steps
 * sit in the glass card the score card replaces, at the same place.
 *
 * While the CV is still being written, the label says so, the line stays
 * empty and no step has started. `ready` marks every step done ("Your CV
 * Health is ready"). `onCancel` adds a quiet "Cancel check" link under the
 * steps; `failed` swaps the stage, line and steps for a heading and "Your CV
 * is unchanged. Try the check again." (the host renders the actions).
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { HealthHeart } from './HealthHeart';
// Explicit `.web`: the mark's look is CSS here, not react-native-svg.
import { HealthStepMark } from './HealthStepMark.web';
import { getStageTextKey } from './cv-health-copy';
import {
  CV_HEALTH_CHECK_STEPS,
  HEALTH_CHECK_WAVE_PATH,
  HEALTH_CHECK_WAVE_SPAN,
  getCheckCount,
  getCheckProgress,
  getCheckStepStates,
} from './health-gauge-geometry';
import type { HealthCheckProgressProps } from './health-props';

export type { HealthCheckProgressProps };

/** On the mark's 20 px grid: the done tick, and the active step's half disc. */

export function HealthCheckProgress({
  report,
  stage,
  waiting = false,
  showIdentity = false,
  showUnchangedNote = false,
  onCancel,
  failed = false,
}: HealthCheckProgressProps) {
  const { t } = useTranslation('common');
  const progress = waiting ? 0 : getCheckProgress(report, stage);
  const count = waiting ? null : getCheckCount(report, stage);
  // Writing comes before the check, so no step has started yet.
  const steps = waiting
    ? CV_HEALTH_CHECK_STEPS.map((step) => ({ step, state: 'pending' as const }))
    : getCheckStepStates(stage);
  const label = waiting ? t('cvHealth.stages.writing') : t(getStageTextKey(stage));
  const ready = stage === 'ready' && !waiting;
  // The AI pass is the one long wait: say so under its step.
  const slow = !waiting && stage === 'skills_experience';

  const identity = showIdentity ? (
    <div className="tabbio-health-identity">
      <HealthHeart
        size={20}
        state={ready || failed ? 'resting' : 'working'}
        color={failed ? 'var(--health-ink-soft)' : undefined}
      />
      <h1>{t('cvHealth.title')}</h1>
    </div>
  ) : null;

  if (failed) {
    return (
      <section
        className="tabbio-health tabbio-health-checking"
        data-failed="true"
        data-card={showIdentity ? 'true' : undefined}
      >
        {identity}
        <h2 className="tabbio-health-check-label" role="status" aria-live="polite" aria-atomic="true">
          {t('cvHealth.stages.failed')}
        </h2>
        <p className="tabbio-health-lead tabbio-health-check-lead">{t('cvHealth.checkSteps.failedLead')}</p>
      </section>
    );
  }

  const line = {
    '--p': `${progress}%`,
    // The gap between the wave and the rest of the track, once both show.
    '--gap': progress > 0 && progress < 100 ? '6px' : '0px',
  } as CSSProperties;

  return (
    <section
      className="tabbio-health tabbio-health-checking"
      data-ready={ready}
      data-card={showIdentity ? 'true' : undefined}
    >
      {identity}
      <h2 className="tabbio-health-check-label" role="status" aria-live="polite" aria-atomic="true">
        {/* A new stage fades and rises in (the key remounts it). */}
        <span key={label} className="tabbio-health-check-label-text">
          {label}
        </span>
      </h2>
      <div
        className="tabbio-health-progress"
        role="progressbar"
        aria-label={t('cvHealth.checkSteps.progressLabel')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
        data-settled={ready ? 'true' : undefined}
        style={line}
      >
        <span className="tabbio-health-progress-rest" />
        <span className="tabbio-health-progress-fill">
          <svg
            className="tabbio-health-progress-wave"
            width={HEALTH_CHECK_WAVE_SPAN}
            height={12}
            viewBox={`0 0 ${HEALTH_CHECK_WAVE_SPAN} 12`}
            aria-hidden="true"
            focusable="false"
          >
            <path d={HEALTH_CHECK_WAVE_PATH} />
          </svg>
        </span>
        <span className="tabbio-health-progress-stop" />
      </div>
      {/* The bar already carries the value for screen readers. Always in the
          layout, so the steps never move when the count arrives. */}
      <p className="tabbio-health-progress-count" aria-hidden="true">
        {count ? (
          <>
            <span>{t('cvHealth.checkSteps.progressLabel')}</span>
            <span dir="ltr">
              {count.completed}/{count.total}
            </span>
          </>
        ) : null}
      </p>
      <ol className="tabbio-health-steps">
        {steps.map(({ step, state }) => (
          <li key={step} data-stage={state} aria-current={state === 'active' ? 'step' : undefined}>
            <HealthStepMark state={state} />
            <span className="tabbio-health-step-text">
              {t(`cvHealth.checkSteps.${step}`)}
              {slow && state === 'active' ? (
                <span className="tabbio-health-step-hint">{t('cvHealth.checkSteps.slowHint')}</span>
              ) : null}
            </span>
          </li>
        ))}
      </ol>
      {showUnchangedNote ? (
        <p className="tabbio-health-small tabbio-health-check-note">
          <MaterialSymbol name="lock" size={14} />
          <span>{t('cvHealth.checkSteps.unchanged')}</span>
        </p>
      ) : null}
      {onCancel && !ready ? (
        <button type="button" className="tabbio-health-btn is-text tabbio-health-check-cancel" onClick={onCancel}>
          {t('cvHealth.checkSteps.cancel')}
        </button>
      ) : null}
    </section>
  );
}

export default HealthCheckProgress;
